import { useEffect, useState } from 'react';

const features = [
  { icon: 'https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/block-one-img-1.svg', text: 'For children 5–12 years old' },
  { icon: 'https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/block-one-img-3.svg', text: 'Getting kids excited about STEM' },
  { icon: 'https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/block-one-img-2.svg', text: 'Learning electronics in a simple way' },
  { icon: 'https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/block-one-img-4.svg', text: 'Building cognitive & hands-on skills' },
];

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gray-900">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.googleapis.com/storage.nanitrobot.com/wp-content/uploads/2026/04/%D0%94%D0%B8%D0%B7%D0%B0%D0%B9%D0%BD-%D0%B1%D0%B5%D0%B7-%D0%BD%D0%B0%D0%B7%D0%B2%D0%B0%D0%BD%D0%B8%D1%8F-1.png"
          alt="Nanit Inclusive hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>
      </div>

      {/* Yellow accent bar */}
      <div className="absolute top-0 left-0 w-1.5 h-full bg-yellow-400 z-10"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
        <div className="max-w-2xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/40 rounded-full px-4 py-1.5 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
            <span className="text-yellow-300 text-xs font-semibold uppercase tracking-widest">STEM Solution for Special Needs</span>
          </div>

          {/* Heading */}
          <h1
            className={`font-syne text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            Nanit<br />
            <span className="text-yellow-400">Inclusive</span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-lg text-white/80 leading-relaxed mb-10 max-w-lg transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            An innovative product designed to develop <strong className="text-white">engineering thinking</strong> and creativity in children with special educational needs.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-wrap gap-4 mb-14 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, 'contact')}
              className="bg-yellow-400 text-gray-900 font-bold px-8 py-3.5 rounded-full hover:bg-yellow-300 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Order Now
            </a>
            <a
              href="#booking"
              onClick={(e) => handleAnchor(e, 'booking')}
              className="border-2 border-white/60 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Book a Meeting
            </a>
            <a
              href="#about"
              onClick={(e) => handleAnchor(e, 'about')}
              className="text-white/70 font-medium px-4 py-3.5 hover:text-white transition-colors cursor-pointer text-sm flex items-center gap-2 whitespace-nowrap"
            >
              Learn more <i className="ri-arrow-down-line"></i>
            </a>
          </div>

          {/* Feature pills */}
          <div
            className={`grid grid-cols-2 gap-3 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {features.map((f) => (
              <div key={f.text} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3">
                <img src={f.icon} alt="" className="w-7 h-7 flex-shrink-0 brightness-0 invert" />
                <span className="text-white/90 text-xs font-medium leading-snug">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs uppercase tracking-widest">Scroll</span>
        <i className="ri-arrow-down-line text-white/40 text-lg"></i>
      </div>
    </section>
  );
}
